/**
 * The short pieces (05 §3), all original: the high-score loop, the game-over sting and the
 * level-complete fanfare.
 */

import { EIGHTHS, MARCH } from './grooves';
import type { Song } from './types';

/**
 * High scores: a triumphant 8-bar loop in G, 152 BPM (12.6 s). Fanfare leaps up the triad in
 * a dotted 3 + 3 + 2 rhythm over a marching bass that rolls its snare into every bar line.
 */
export const HISCORE: Song = {
  bpm: 152,
  loop: true,
  bassFloor: 36,
  arpFloor: 55,
  mix: { lead: 0.2, arp: 0.08, bass: 0.25, drum: 0.3 },
  order: ['fanfare'],
  patterns: {
    fanfare: {
      chords: 'G | C | G | D | Em | C | D | D7',
      lead: `
        G4 . . B4 . . D5 . G5 . . . D5 . . . |
        E5 . . . G5 . . . C6 . . . G5 . . . |
        B5 . . A5 . . G5 . D5 . . . B4 . . . |
        A4 . . . D5 . . . F#5 . . . A5 . . . |
        B5 . . . . . G5 . E5 . . . B4 . . . |
        C6 . . . . . G5 . E5 . . . C5 . . . |
        D5 . F#5 . A5 . D6 . . . . . A5 . . . |
        C6 . . . A5 . . . F#5 . . . D5 . . .`,
      bass: { groove: MARCH },
      arp: EIGHTHS,
    },
  },
};

/**
 * Game over: four bars falling a step at a time from C6 to C5 through A-flat, F minor and G
 * to C minor, 108 BPM (8.9 s), played once.
 */
export const GAMEOVER: Song = {
  bpm: 108,
  loop: false,
  bassFloor: 36,
  arpFloor: 55,
  mix: { lead: 0.2, arp: 0.08, bass: 0.26, drum: 0.25 },
  order: ['fall'],
  patterns: {
    fall: {
      chords: 'Ab | Fm | G | Cm',
      lead: `
        C6 . . . . . . . Bb5 . . . Ab5 . . . |
        G5 . . . . . . . F5 . . . Eb5 . . . |
        D5 . . . . . . . B4 . . . G4 . . . |
        C5 . . . . . . . . . . . . . . -`,
      bass: `
        Ab2 . . . . . . . Ab2 . . . . . . . |
        F2 . . . . . . . F2 . . . . . . . |
        G2 . . . . . . . G1 . . . . . . . |
        C2 . . . . . . . . . . . . . . -`,
    },
  },
};

/**
 * A level complete: a four-bar fanfare in C, 140 BPM (6.9 s), played once. Two bars climbing
 * over pumping chords, then a held top C over a ringing chord and a snare roll.
 */
export const COMPLETE: Song = {
  bpm: 140,
  loop: false,
  bassFloor: 36,
  arpFloor: 55,
  mix: { lead: 0.22, arp: 0.09, bass: 0.26, drum: 0.3 },
  order: ['climb', 'ring'],
  patterns: {
    climb: {
      chords: 'C | F G',
      lead: `
        G4 . C5 . E5 . G5 . . . E5 . G5 . . . |
        A5 . . . F5 . A5 . B5 . . . G5 . B5 .`,
      bass: `
        C2 . . . C3 . . . C2 . . . C3 . . . |
        F2 . . . F3 . . . G2 . . . G3 . . .`,
      arp: EIGHTHS,
    },
    ring: {
      chords: 'C | C',
      lead: `
        C6 . . . . . . . . . . . . . . . |
        G5 . C6 . - . . . . . . . . . . .`,
      bass: `
        C3 . . . . . . . S . S . S S S S |
        C2 . . . S . . . . . . . . . . -`,
    },
  },
};
